import {
  Beaker,
  ChevronDown,
  Download,
  FileText,
  FileWarning,
  FlaskConical,
  MoreVertical,
  Pill,
  Stethoscope,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GetReservationDiagnoses } from '@virtual-care/client';
import { formatDate } from '@virtual-care/isomorphic';
import { DownloadButton } from '@virtual-care/media-web';
import {
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@virtual-care/shadcn';

type Diagnosis = GetReservationDiagnoses[number];

export function DiagnosisCards({
  diagnoses,
}: {
  diagnoses?: GetReservationDiagnoses;
}) {
  const { t } = useTranslation();
  if (!diagnoses || diagnoses.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="flex items-center gap-1.5 text-base font-bold">
        <FileText className="h-5 w-5 text-gray-600" />
        {t('appointments.detail.diagnosisCards.sectionTitle')}
      </h3>
      {diagnoses.map((diagnosis, index) => (
        <DiagnosisCard
          key={diagnosis.id}
          diagnosis={diagnosis}
          visitNumber={diagnoses.length - index}
        />
      ))}
    </div>
  );
}

function DiagnosisCard({
  diagnosis,
  visitNumber,
}: {
  diagnosis: Diagnosis;
  visitNumber: number;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const hasMedications = diagnosis.prescriptions.length > 0;
  const hasLabTests = diagnosis.labTests.length > 0;
  const hasIcd10 = diagnosis.icd10Codes.length > 0;
  const hasSickLeave = diagnosis.leaves > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <div className="flex items-center justify-between p-4">
          <CollapsibleTrigger asChild>
            <button className="flex flex-1 items-center gap-2 text-start">
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
              <div>
                <p className="text-sm font-semibold">
                  {t('appointments.detail.diagnosisCards.visit', {
                    number: visitNumber,
                  })}
                </p>
                <p className="text-muted-foreground text-xs">
                  {formatDate(diagnosis.createdAt)}
                </p>
              </div>
            </button>
          </CollapsibleTrigger>
          <PDFDropdownMenu
            diagnosisId={diagnosis.id}
            hasMedications={hasMedications}
            hasLabTests={hasLabTests}
            hasSickLeave={hasSickLeave}
          />
        </div>
        <CollapsibleContent>
          <CardContent className="border-t pt-4">
            <div className="space-y-4">
              <PrescriptionsSection
                prescriptions={diagnosis.prescriptions}
                notes={diagnosis.prescriptionNotes}
              />
              <LabTestsSection
                labTests={diagnosis.labTests}
                notes={diagnosis.labTestNotes}
              />
              <ICD10Section
                icd10Codes={diagnosis.icd10Codes}
                notes={diagnosis.icd10Notes}
              />
              {!hasMedications && !hasLabTests && !hasIcd10 && (
                <p className="text-muted-foreground text-sm">
                  {t('appointments.detail.diagnosisCards.noResults')}
                </p>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function PDFDropdownMenu({
  diagnosisId,
  hasMedications,
  hasLabTests,
  hasSickLeave,
}: {
  diagnosisId: string;
  hasMedications: boolean;
  hasLabTests: boolean;
  hasSickLeave: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DownloadButton
          endpoint="GET /diagnosis/{id}/report.pdf"
          input={{ id: diagnosisId }}
          filename={`medical-report-${diagnosisId}.pdf`}
          icon={<Download className="h-4 w-4" />}
          label={t('appointments.detail.diagnosisCards.downloadReport')}
          variant="ghost"
          className="w-full justify-start"
        />
        {hasMedications && (
          <DownloadButton
            endpoint="GET /diagnosis/{id}/prescription.pdf"
            input={{ id: diagnosisId }}
            filename={`prescription-${diagnosisId}.pdf`}
            icon={<Pill className="h-4 w-4" />}
            label={t('appointments.detail.diagnosisCards.downloadPrescription')}
            variant="ghost"
            className="w-full justify-start"
          />
        )}
        {hasLabTests && (
          <DownloadButton
            endpoint="GET /diagnosis/{id}/labtests.pdf"
            input={{ id: diagnosisId }}
            filename={`labtests-${diagnosisId}.pdf`}
            icon={<FlaskConical className="h-4 w-4" />}
            label={t('appointments.detail.diagnosisCards.downloadLabTests')}
            variant="ghost"
            className="w-full justify-start"
          />
        )}
        {hasSickLeave && (
          <DownloadButton
            endpoint="GET /diagnosis/{id}/sick-leave.pdf"
            input={{ id: diagnosisId }}
            filename={`sick-leave-${diagnosisId}.pdf`}
            icon={<FileWarning className="h-4 w-4" />}
            label={t('appointments.detail.diagnosisCards.downloadSickLeave')}
            variant="ghost"
            className="w-full justify-start"
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PrescriptionsSection({
  prescriptions,
  notes,
}: {
  prescriptions: Diagnosis['prescriptions'];
  notes: string;
}) {
  const { t } = useTranslation();
  if (prescriptions.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-blue-600">
        <Pill className="h-4 w-4" />
        {t('appointments.detail.diagnosisCards.medications')}
      </h4>
      <div className="space-y-2">
        {prescriptions.map((prescription) => (
          <div key={prescription.id}>
            <p className="flex items-center gap-2 text-sm font-medium">
              <span className="text-blue-600">&larr;</span>
              {prescription.medication.brandName}
            </p>
            <p className="text-muted-foreground ps-5 text-xs">
              {t('appointments.detail.diagnosisCards.prescriptionDetails', {
                strength: prescription.medication.strength.name,
                unit: prescription.medication.unit.name,
                dosage: prescription.dosageAmount,
                period: prescription.period,
                form: prescription.medication.dosageForm.name,
              })}
            </p>
          </div>
        ))}
      </div>
      {notes && <p className="text-muted-foreground text-xs">{notes}</p>}
    </div>
  );
}

function LabTestsSection({
  labTests,
  notes,
}: {
  labTests: Diagnosis['labTests'];
  notes: string;
}) {
  const { t } = useTranslation();
  if (labTests.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-green-600">
        <Beaker className="h-4 w-4" />
        {t('appointments.detail.diagnosisCards.labTests')}
      </h4>
      <ul className="space-y-1">
        {labTests.map((test) => (
          <li key={test.id} className="flex items-center gap-2 text-sm">
            <span className="text-green-600">&larr;</span>
            {test.labTest.name}
          </li>
        ))}
      </ul>
      {notes && <p className="text-muted-foreground text-xs">{notes}</p>}
    </div>
  );
}

function ICD10Section({
  icd10Codes,
  notes,
}: {
  icd10Codes: Diagnosis['icd10Codes'];
  notes: string;
}) {
  const { t } = useTranslation();
  if (icd10Codes.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-purple-600">
        <Stethoscope className="h-4 w-4" />
        {t('appointments.detail.diagnosisCards.icd10')}
      </h4>
      <ul className="space-y-1">
        {icd10Codes.map((item) => (
          <li key={item.id} className="flex items-center gap-2 text-sm">
            <span className="text-purple-600">&larr;</span>
            {item.icd10Code.code} - {item.icd10Code.description}
          </li>
        ))}
      </ul>
      {notes && <p className="text-muted-foreground text-xs">{notes}</p>}
    </div>
  );
}
